import React, { useState, useEffect, useRef, useCallback } from "react";
import NotificationAlert from "react-notification-alert";

import {
  Button,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Spinner,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { Form } from "@unform/web";

import Select from "../../../components/Form/SelectInput";
import Checkbox from "../../../components/Checkbox";

import apiGd from "../../../../services/apiGd";
import api from "services/api";

// Estado inicial dos selects de marca. `key` força o remount do Select para
// que o novo defaultValue (a marca padrão) seja aplicado.
const EMPTY_BRAND_STATE = { key: 'empty', options: [], defaultOption: null };

// Marca de destino da importação do iFood: sempre derivada do vínculo com a
// loja escolhida, nunca escolhida à mão.
const EMPTY_MERCHANT_BRAND = { key: 'empty', option: null, usingDefault: false };

function MenuClone() {
  const notificationAlert = useRef(null);

  const notify = (place, color, message) => {
    var options = {};
    options = {
      place: place,
      message: (
        <div>
          <div>{message}</div>
        </div>
      ),
      type: color,
      icon: "now-ui-icons ui-1_bell-53",
      autoDismiss: 7,
    };
    notificationAlert.current.notificationAlert(options);
  };

  const tokenGd = localStorage.getItem("@QRExpress:tokenGd");
  const token = localStorage.getItem("@QRExpress:token");

  let [restaurantsGD, setRestaurantsGD] = useState([]);
  let [restaurantsWithIfood, setRestaurantsWithIfood] = useState([]);
  let [merchants, setMerchants] = useState([]);

  // Marcas de origem/destino da clonagem entre restaurantes do GD e marca de
  // destino da importação do iFood (o iFood não tem marca de origem).
  let [cloneSourceBrands, setCloneSourceBrands] = useState(EMPTY_BRAND_STATE);
  let [cloneTargetBrands, setCloneTargetBrands] = useState(EMPTY_BRAND_STATE);
  let [ifoodBrands, setIfoodBrands] = useState(EMPTY_BRAND_STATE);
  let [ifoodRestaurantId, setIfoodRestaurantId] = useState(null);
  let [ifoodMerchant, setIfoodMerchant] = useState(null);
  let [ifoodMerchantBrand, setIfoodMerchantBrand] =
    useState(EMPTY_MERCHANT_BRAND);

  let [deleteModalData, setDeleteModalData] = useState(null);
  let [cloneConfirmModalData, setCloneConfirmModalData] = useState(null);

  let [disableCloneBtn, setDisableCloneBtn] = useState(false);
  let [disableCloneIfood, setDisableCloneIfood] = useState(false);

  const getRestaurantsGD = useCallback(async () => {
    try {
      const response = await apiGd.get(
        "admin/restaurants?count_sessions=false&simple=true",
        {
          headers: { Authorization: `Bearer ${tokenGd}` },
        },
      );

      const resturants = response.data.filter((r) => !r.deleted_at);

      const options = resturants.map((restaurant) => {
        return {
          label: restaurant.name,
          value: restaurant.id,
        };
      });

      setRestaurantsGD(options);

      const options2 = resturants
        .filter((restaurant) => restaurant.has_ifood)
        .map((restaurant) => {
          return {
            label: restaurant.name,
            value: restaurant.id,
          };
        });

      setRestaurantsWithIfood(options2);
    } catch (err) {
      notify("tr", "danger", "Erro ao carregar informações.");
    }
  }, [tokenGd]);

  // Carrega as marcas não deletadas do restaurante e já deixa a marca padrão
  // (is_default) selecionada.
  async function getBrands(restaurant_id, setBrands) {
    if (!restaurant_id) {
      setBrands(EMPTY_BRAND_STATE);
      return;
    }

    try {
      const response = await apiGd.get(
        `admin/restaurants/${restaurant_id}/brands`,
        {
          headers: { Authorization: `Bearer ${tokenGd}` },
        }
      );

      const brands = response.data.filter((brand) => !brand.deleted_at);

      const options = brands.map((brand) => {
        return {
          label: brand.is_default
            ? `${brand.fantasy_name || brand.name} (padrão)`
            : brand.fantasy_name || brand.name,
          value: brand.id,
        };
      });

      const defaultBrand = brands.find((brand) => brand.is_default);

      setBrands({
        key: `${restaurant_id}-${options.length}`,
        options,
        defaultOption:
          options.find((option) => option.value === defaultBrand?.id) || null,
      });
    } catch (error) {
      notify('tr', 'danger', 'Erro ao carregar as marcas do restaurante.');
      setBrands(EMPTY_BRAND_STATE);
    }
  }

  async function deleteWholeMenu(data) {
    const { restaurant_id } = data;

    if (!restaurant_id) {
      notify("tr", "warning", "Escolha o restuarante para deletar o menu.");
      return;
    }

    try {
      await apiGd.put(
        "admin/restaurants/menu/delete",
        { restaurant_id },
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      setDeleteModalData(null);
      notify("tr", "success", "Menu deletado com sucesso.");
    } catch (error) {
      setDeleteModalData(null);
      notify("tr", "danger", "Erro ao deletar.");
    }
  }

  async function cloneFromGDToGD(data) {
    const { restaurant_id1, restaurant_id2, brand_id, source_brand_id } = data;

    if (!restaurant_id1 || !restaurant_id2) {
      notify("tr", "warning", "Escolha os dois restuarantes.");
      return;
    }

    if (restaurant_id1 === restaurant_id2) {
      notify(
        "tr",
        "danger",
        "Os restaurantes de origem e destino não podem ser iguais.",
      );
      return;
    }

    if (!source_brand_id) {
      notify('tr', 'warning', 'Escolha a marca de origem.');
      return;
    }

    if (!brand_id) {
      notify('tr', 'warning', 'Escolha a marca de destino.');
      return;
    }

    try {
      const testMenuResponse = await apiGd.get(
        `admin/restaurants/menu/test/${restaurant_id2}`,
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      const { empty } = testMenuResponse.data;

      if (!empty) {
        setCloneConfirmModalData(data);
        return;
      }

      setDisableCloneBtn(true);

      await apiGd.post(
        `admin/restaurants/clone-menu`,
        { restaurant_id1, restaurant_id2, brand_id, source_brand_id },
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      setDisableCloneBtn(false);

      notify("tr", "success", "Clonagem feita com sucesso.");
    } catch (error) {
      setDisableCloneBtn(false);
      setCloneConfirmModalData(null);

      notify("tr", "danger", "Erro ao clonar.");
    }
  }

  async function cloneFromGDToGDWithoutTests(data) {
    const { restaurant_id1, restaurant_id2, brand_id, source_brand_id } = data;

    try {
      setCloneConfirmModalData(null);
      setDisableCloneBtn(true);

      await apiGd.post(
        `admin/restaurants/clone-menu`,
        { restaurant_id1, restaurant_id2, brand_id, source_brand_id },
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      setDisableCloneBtn(false);

      notify("tr", "success", "Clonagem feita com sucesso.");
    } catch (error) {
      setDisableCloneBtn(false);
      setCloneConfirmModalData(null);

      notify("tr", "danger", "Erro ao clonar.");
    }
  }

  async function getMechants(restaurant_id) {
    if (!restaurant_id) {
      setMerchants([]);
      return;
    }

    try {
      const response = await apiGd.get(
        `admin/ifood-merchants/${restaurant_id}`,
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      const options = response.data.map((m) => {
        return {
          label: m.name,
          value: m.ifood_merchant_id,
          // Marca do vínculo: é ela que define em qual marca os pedidos desse
          // merchant entram, e portanto a única marca em que faz sentido
          // importar o cardápio dele.
          brand_id: m.brand_id,
        };
      });

      setMerchants(options);
    } catch (error) {
      notify("tr", "danger", "Erro ao consultar os Merchants.");
      setMerchants([]);
    }
  }

  // Fixa a marca de destino da importação na marca vinculada à loja iFood
  // escolhida. Vínculos legados podem estar sem marca — nesse caso cai na marca
  // padrão do restaurante, mesmo fallback que o backend aplica. Depende também
  // de `ifoodBrands` porque as marcas e os merchants carregam em paralelo, e a
  // loja pode ser escolhida antes de a lista de marcas chegar.
  useEffect(() => {
    if (!ifoodMerchant) {
      setIfoodMerchantBrand(EMPTY_MERCHANT_BRAND);
      return;
    }

    const linkedOption =
      ifoodBrands.options.find(
        (option) => option.value === ifoodMerchant.brand_id
      ) || null;

    const option = linkedOption || ifoodBrands.defaultOption;

    if (!option) {
      setIfoodMerchantBrand(EMPTY_MERCHANT_BRAND);
      return;
    }

    setIfoodMerchantBrand({
      key: `${ifoodMerchant.value}-${option.value}`,
      option,
      usingDefault: !linkedOption,
    });
  }, [ifoodMerchant, ifoodBrands]);

  async function importIfood(data) {
    const { restaurant_id, merchant_id, associate, brand_id } = data;

    if (!restaurant_id || !merchant_id) {
      notify("tr", "danger", "Escolha o restaurante e a loja iFood!");
      return;
    }

    if (!brand_id) {
      notify(
        'tr',
        'warning',
        'A loja iFood escolhida não tem marca definida. Verifique o vínculo antes de importar.'
      );
      return;
    }

    try {
      const response = await apiGd.post(
        "admin/import-menu/ifood",
        {
          restaurant_id,
          merchant_id,
          associate,
          brand_id,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenGd}`,
          },
        },
      );

      notify('tr', 'success', 'Cardápio importado com sucesso.');
    } catch (error) {
      setDisableCloneIfood(false);
      notify("tr", "danger", "Erro ao importar cardápio.");
      setMerchants([]);
    }
  }

  useEffect(() => {
    getRestaurantsGD();
  }, [getRestaurantsGD]);

  return (
    <>
      <NotificationAlert ref={notificationAlert} />

      <div
        className="content"
        style={{ marginLeft: "50px", marginRight: "50px" }}
      >
        <Form onSubmit={(data) => setDeleteModalData(data)}>
          <Card body>
            <CardBody>
              <Row>
                <Col md="12">
                  <h3>Apagar cardápio de um restaurante</h3>
                </Col>
              </Row>

              <Row>
                <Col md="10">
                  <FormGroup>
                    <Select
                      label="Escolha o restaurante"
                      name="restaurant_id"
                      options={restaurantsGD}
                    />
                  </FormGroup>
                </Col>

                <Col
                  md="2"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "end",
                  }}
                >
                  <Button color="danger" type="submit">
                    {" "}
                    Apagar{" "}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>

        <Form onSubmit={cloneFromGDToGD}>
          <Card body>
            <CardBody>
              <Row>
                <Col md="12">
                  <h3>Clonar cardápio entre restaurantes do GD</h3>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Select
                      label="Escolha o restaurante modelo"
                      name="restaurant_id1"
                      options={restaurantsGD}
                      onChange={(e) => getBrands(e?.value, setCloneSourceBrands)}
                    />
                  </FormGroup>
                </Col>

               <Col md="6">
                 <FormGroup>
                   <Select
                      key={cloneSourceBrands.key}
                      label="Escolha a marca de origem"
                      name="source_brand_id"
                      options={cloneSourceBrands.options}
                      defaultValue={cloneSourceBrands.defaultOption}
                      isDisabled={cloneSourceBrands.options.length === 0}
                      placeholder="Selecione o restaurante modelo..."
                    />
                 </FormGroup>
               </Col>
              </Row>

              <Row>
               <Col md="6">
                 <FormGroup>
                   <Select
                      label="Escolha o restaurante destino"
                      name="restaurant_id2"
                      options={restaurantsGD}
                      onChange={(e) => getBrands(e?.value, setCloneTargetBrands)}
                    />
                 </FormGroup>
               </Col>

               <Col md="6">
                 <FormGroup>
                   <Select
                      key={cloneTargetBrands.key}
                      label="Escolha a marca de destino"
                      name="brand_id"
                      options={cloneTargetBrands.options}
                      defaultValue={cloneTargetBrands.defaultOption}
                      isDisabled={cloneTargetBrands.options.length === 0}
                      placeholder="Selecione o restaurante destino..."
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col
                  md="12"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    disabled={disableCloneBtn}
                    color="success"
                    type="submit"
                  >
                    Clonar
                    {disableCloneBtn && (
                      <>
                        {" "}
                        <Spinner size="sm" color="white" />
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>

        <Form onSubmit={importIfood}>
          <Card body>
            <CardBody>
              <Row>
                <Col md="12">
                  <h3>Importar cardápio do iFood</h3>
                </Col>
              </Row>

              <Row>
                <Col md="4">
                  <FormGroup>
                    <Select
                      label="Escolha o restaurante destino"
                      name="restaurant_id"
                      options={restaurantsWithIfood}
                      onChange={(e) => {
                        getMechants(e?.value);
                        getBrands(e?.value, setIfoodBrands);
                        setIfoodRestaurantId(e?.value ?? null);
                        setIfoodMerchant(null);
                      }}
                    />
                  </FormGroup>
                </Col>

               <Col md="4">
                 <FormGroup>
                   <Select
                      // Remonta ao trocar de restaurante para não manter
                      // selecionada uma loja iFood que não é mais da lista.
                      key={`merchant-${ifoodRestaurantId ?? 'none'}`}
                      label="Escolha a loja iFood"
                      name="merchant_id"
                      options={merchants}
                      onChange={(e) => setIfoodMerchant(e)}
                    />
                 </FormGroup>
               </Col>

               <Col md="4">
                 <FormGroup>
                   <Select
                      key={ifoodMerchantBrand.key}
                      label="Marca de destino"
                      name="brand_id"
                      options={ifoodBrands.options}
                      defaultValue={ifoodMerchantBrand.option}
                      isDisabled
                      placeholder="Selecione a loja iFood..."
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <small style={{ color: '#8b8b8b' }}>
                    A marca de destino é a que está vinculada à loja iFood
                    escolhida — é nela que os pedidos desse merchant entram, então
                    o cardápio só pode ser importado nela. Para importar em outra
                    marca, altere o vínculo da loja iFood.
                    {ifoodMerchantBrand.option && ifoodMerchantBrand.usingDefault && (
                      <>
                        {' '}
                        <b>
                          Esta loja iFood está sem marca vinculada; será usada a
                          marca padrão do restaurante.
                        </b>
                      </>
                    )}
                  </small>
                </Col>
              </Row>

              <Row style={{ marginTop: '15px' }}>
                <Col md="6">
                  <Checkbox
                    label="Vincular itens ao iFood"
                    name="associate"
                    defaultChecked
                  />
                </Col>
              </Row>

              <Row>
                <Col
                  md="12"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    disabled={disableCloneIfood}
                    color="success"
                    type="submit"
                  >
                    Clonar
                    {disableCloneIfood && (
                      <>
                        {" "}
                        <Spinner size="sm" color="white" />
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </div>

      <Modal isOpen={deleteModalData} toggle={() => setDeleteModalData(null)}>
        <ModalHeader> Apagar Cardápio </ModalHeader>

        <ModalBody>
          <Container>
            <p>
              Essa operação deletará os todos os{" "}
              <i style={{ color: "blue" }}>produtos, complementos e insumos</i>{" "}
              associados.
            </p>
            <p>Deseja realmente deletar o cardápio desse restaurante?</p>
          </Container>
        </ModalBody>

        <ModalFooter>
          <Button
            color="primary"
            onClick={() => deleteWholeMenu(deleteModalData)}
          >
            Confirmar
          </Button>{" "}
          <Button
            color="danger"
            style={{ marginRight: "10px" }}
            onClick={() => setDeleteModalData(null)}
          >
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cloneConfirmModalData != null}
        toggle={() => setCloneConfirmModalData(null)}
      >
        <ModalHeader> Clonar Cardápio </ModalHeader>

        <ModalBody>
          <Container>
            <p>
              O restaurante de destino possui produtos, complementos ou
              categorias de produtos e complementos cadastrados. Pode ocorrer
              duplicação de nomes após a clonagem.
            </p>
            <p>Tem certeza que deseja clonar?</p>
          </Container>
        </ModalBody>

        <ModalFooter>
          <Button
            color="primary"
            onClick={() => cloneFromGDToGDWithoutTests(cloneConfirmModalData)}
          >
            Confirmar
          </Button>{" "}
          <Button
            color="danger"
            style={{ marginRight: "10px" }}
            onClick={() => setCloneConfirmModalData(null)}
          >
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default MenuClone;
